import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, timeout, TimeoutError } from 'rxjs';
import { injectRuntimeConfig } from '../core/config/runtime-config.token';
import { buildBackendUrl } from '../core/config/runtime-config.provider';
import { APP_ROUTE_PATHS } from '../core/routing/app-route-paths';
import { ConnectionFacade } from '../core/state/connection.store';
import { DriversFacade } from '../core/state/drivers.store';
import { OrdersFacade } from '../core/state/orders.store';
import { ReferenceDataFacade } from '../core/state/reference-data.store';
import { SessionFacade } from '../core/state/session.store';
import { ShuttleFacade } from '../core/state/shuttle.store';
import { RealtimeBootstrapCoordinatorService } from '../realtime/realtime-bootstrap-coordinator.service';
import { LoginCredentials, LoginRequestPayload, PersistedSessionRecord } from './auth.models';
import { AuthStorageService } from './auth-storage.service';
import { DeviceMetadataService } from './device-metadata.service';
import { MonitorBackendApiService } from '../shared-integrations/monitor-backend-api.service';

// If the backend accepts the connection but never answers (observed on prod), the sign-in button would
// otherwise stay on "signing in" forever.
export const LOGIN_TIMEOUT_MS = 20_000;

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly runtimeConfig = injectRuntimeConfig();
  private readonly sessionFacade = inject(SessionFacade);
  private readonly referenceDataFacade = inject(ReferenceDataFacade);
  private readonly ordersFacade = inject(OrdersFacade);
  private readonly driversFacade = inject(DriversFacade);
  private readonly connectionFacade = inject(ConnectionFacade);
  private readonly shuttleFacade = inject(ShuttleFacade);
  private readonly authStorage = inject(AuthStorageService);
  private readonly deviceMetadata = inject(DeviceMetadataService);
  private readonly backendApi = inject(MonitorBackendApiService);
  private readonly realtimeBootstrapCoordinator = inject(RealtimeBootstrapCoordinatorService);

  async login(credentials: LoginCredentials): Promise<void> {
    const payload = this.buildLoginPayload(credentials);
    const response = await firstValueFrom(
      this.http
        .post(buildBackendUrl(this.runtimeConfig, 'login'), payload, {
          observe: 'response'
        })
        .pipe(timeout(LOGIN_TIMEOUT_MS))
    ).catch((error: HttpErrorResponse | TimeoutError) => {
      throw new Error(this.normalizeLoginError(error));
    });

    const persistedSession = this.createPersistedSessionRecord(response, credentials.mobile);
    this.applySession(persistedSession, 'login');
    await this.router.navigateByUrl(`/${APP_ROUTE_PATHS.operator}`);
  }

  restoreFromStorage(): void {
    const persistedSession = this.authStorage.readSession();

    if (!persistedSession) {
      return;
    }

    this.applySession(persistedSession, 'restore');
  }

  async logout(): Promise<void> {
    this.authStorage.clearSession();
    this.sessionFacade.clearSession();
    this.referenceDataFacade.clearReferenceData();
    this.ordersFacade.clear();
    this.driversFacade.clear();
    this.shuttleFacade.clear();
    this.connectionFacade.reset();
    this.realtimeBootstrapCoordinator.clear();

    await this.router.navigateByUrl(`/${APP_ROUTE_PATHS.signIn}`);
  }

  private applySession(
    persistedSession: PersistedSessionRecord,
    source: 'login' | 'restore'
  ): void {
    this.sessionFacade.restoreSession(persistedSession);
    this.authStorage.writeSession(persistedSession);
    this.connectionFacade.reset();
    this.realtimeBootstrapCoordinator.requestBootstrap(source);
    void this.refreshAccountName();
  }

  private async refreshAccountName(): Promise<void> {
    try {
      const account = await this.backendApi.getMyAccount();
      const fullName = [account.firstName, account.lastName].filter(Boolean).join(' ').trim();
      this.sessionFacade.setAccountName(fullName || null);
    } catch {
      // Non-critical: the header falls back to the operator's mobile/"ggAdmin" label.
      this.sessionFacade.setAccountName(null);
    }
  }

  private buildLoginPayload(credentials: LoginCredentials): LoginRequestPayload {
    const descriptor = this.deviceMetadata.buildDescriptor();

    return {
      mobile: credentials.mobile.trim(),
      password: credentials.password,
      platform: 'ADMIN',
      platformVersion: descriptor.platformVersion,
      deviceName: descriptor.deviceName,
      deviceUUID: descriptor.deviceUUID,
      appVersion: descriptor.appVersion
    };
  }

  private createPersistedSessionRecord(
    response: HttpResponse<unknown>,
    mobile: string
  ): PersistedSessionRecord {
    const authToken = response.headers.get('x-auth-token') ?? response.headers.get('X-Auth-Token');
    const userId = response.headers.get('x-auth-userid') ?? response.headers.get('X-Auth-UserId');

    if (!authToken || !userId) {
      throw new Error("Oh snap! You don't have persmission to access this page!");
    }

    return {
      authToken,
      userId,
      operatorLabel: mobile.trim() || null,
      restoredAt: new Date().toISOString()
    };
  }

  private normalizeLoginError(error: HttpErrorResponse | TimeoutError): string {
    if (error instanceof TimeoutError) {
      return 'The server is not responding. Please try again in a moment.';
    }

    if (error.error && typeof error.error === 'object' && 'error_msg' in error.error) {
      return String(error.error.error_msg);
    }

    if (error.status === 0) {
      return 'Login request was blocked before reaching the backend. This is usually a CORS or network issue for the current origin.';
    }

    return 'Login failed.';
  }
}
